import {
  Building2,
  Package,
  HelpCircle,
  Megaphone,
  Code2,
  GraduationCap,
} from "lucide-react"

interface UseCase {
  icon: React.ReactNode
  title: string
  description: string
  examples: string[]
}

const useCases: UseCase[] = [
  {
    icon: <Building2 className="h-6 w-6" />,
    title: "Company Documentation",
    description: "Centralize HR policies, procedures, and company guidelines.",
    examples: ["Employee handbooks", "Onboarding docs", "Company policies"],
  },
  {
    icon: <Package className="h-6 w-6" />,
    title: "Product Knowledge",
    description: "Store product features, specifications, and pricing info.",
    examples: ["Feature lists", "Product specs", "Pricing details"],
  },
  {
    icon: <HelpCircle className="h-6 w-6" />,
    title: "Customer FAQs",
    description: "Common questions, troubleshooting guides, and solutions.",
    examples: ["FAQs", "Troubleshooting", "How-to guides"],
  },
  {
    icon: <Megaphone className="h-6 w-6" />,
    title: "Sales Playbooks",
    description: "Sales scripts, objection handling, and competitive intel.",
    examples: ["Sales scripts", "Objection handling", "Battle cards"],
  },
  {
    icon: <Code2 className="h-6 w-6" />,
    title: "Technical Docs",
    description: "API references, integration guides, and developer docs.",
    examples: ["API docs", "Integration guides", "SDK references"],
  },
  {
    icon: <GraduationCap className="h-6 w-6" />,
    title: "Training Materials",
    description: "Onboarding content, training courses, and certifications.",
    examples: ["Training modules", "Certifications", "Skill guides"],
  },
]

/**
 * Use cases section displaying common knowledge base applications.
 * Shows a grid of use case cards with icons and examples.
 */
export function UseCasesSection() {
  return (
    <section className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold text-neutral-900 lg:text-4xl">
            Knowledge Bases for Every Team
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-neutral-600">
            From customer support to sales, engineering to HR - build knowledge bases
            that power AI agents across your entire organization.
          </p>
        </div>

        {/* Use Cases Grid */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {useCases.map((useCase, index) => (
            <div
              key={index}
              className="group rounded-xl border border-neutral-200 bg-white p-6 transition-all hover:border-primary/30 hover:shadow-lg"
            >
              {/* Icon */}
              <div className="mb-4 inline-flex rounded-xl bg-primary/10 p-3 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                {useCase.icon}
              </div>

              {/* Title */}
              <h3 className="mb-2 text-lg font-semibold text-neutral-900">
                {useCase.title}
              </h3>

              {/* Description */}
              <p className="mb-4 text-sm text-neutral-600">{useCase.description}</p>

              {/* Examples */}
              <div className="flex flex-wrap gap-2">
                {useCase.examples.map((example, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs text-neutral-600"
                  >
                    {example}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
